// src/pages/HistoryVision.jsx
import React from "react";
import { Box, Container, Typography } from "@mui/material";
import { alpha } from "@mui/material/styles";
import { getPhilosophyText } from "./HistoryPhilosophy";

/* ------------ language data ------------ */

const VISION_TEXT = {
  ZH: {
    title: "企業願景",
    bodyLines: [
      "堅持提供優質生活品質為導向的嚴格管理，實現穩健成長邁向成為引領亞洲的全球性公司目標。",
      "積極拓展業務，透過在核心分銷和增強了國內外核心業務的競爭力。",
      "展望未來，我們將繼續透過對核心業務的投資和併購，追求均衡的內生成長，並努力成為名副其實的全球性企業。",
      "為了永續成長方向轉向品質成長，2026年宣布了新的願景「永續價值創造者」理念是透過SEHO品牌在客戶的每個階段為其提供優質商品品質與選擇權。",
    ],
  },
  EN: {
    title: "Corporate Vision",
    bodyLines: [
      "We pursue disciplined management to enhance quality of life and achieve sustainable growth.",
      "By expanding our business, strengthening distribution, and improving competitiveness at home and abroad, we are building a solid foundation for long-term development.",
      "We will continue investing in strategic opportunities to advance toward becoming a truly global enterprise.",
      "Our 2026 vision, “Creator of Sustainable Value,” reflects SEHO's commitment to quality-driven growth and to providing high-quality products and meaningful choices at every stage of life.",
    ],
  },
  JP: {
    title: "企業ビジョン",
    bodyLines: [
      "当社は、より良い生活の質を追求するという考え方を軸に、",
      "厳格なマネジメントに取り組んでいます。",
      "着実な成長を実現し、アジアをリードするグローバル企業へと",
      "飛躍することを目標としています。",
      "",
      "コアとなる流通機能を強化し、国内外の中核事業の競争力を高めることで、",
      "積極的な事業拡大と長期的な成長基盤の構築を進めています。",
      "今後もコア事業への継続的な投資を行い、",
      "バランスの取れた内発的成長を追求し、",
      "信頼されるグローバル企業となることを目指します。",
      "持続的な成長を実現するため、量的拡大から質的成長へと舵を切り、",
      "2026年には新たなビジョン「サステナブル・バリュー・",
      "クリエイター」を掲げました。",
      "",
      "ブランド「SEHO」を通じて、お客様のあらゆる事業段階において、",
      "高品質な製品・サービスと明確な選択肢を提供していくことが、",
      "当社の基本的な考え方です。",
    ],
  },
};

function getVisionText(lang = "ZH") {
  return VISION_TEXT[lang] || VISION_TEXT.ZH;
}

export default function HistoryVision({
  mode = "b2b",
  bg = "background.default",
  rail,
  fonts,
  lang = "ZH",
}) {
  const accent = rail || "#0090A0";

  const backgroundImage =
    "/img/aboutus/history/vision-bg.webp";

  const centerImage =
    "/img/aboutus/history/vision-center.png";

  const { title, bodyLines } = getVisionText(lang);

  const philosophy =
    getPhilosophyText(lang);

  return (
    <Box sx={{ bgcolor: bg }}>
      <Box
        component="section"
        sx={{
          position: "relative",

          /*
           * Keep the section tall enough for large screens so it does
           * not leave a white gap before the footer.
           *
           * The image itself is displayed with backgroundSize: contain
           * below, so it will not be cropped.
           */
          minHeight: {
            xs: "100dvh",
            md: "100dvh",
          },

          overflow: "hidden",

          backgroundImage:
            `url(${backgroundImage})`,

          backgroundRepeat:
            "no-repeat",

          backgroundSize:
            "cover",

          backgroundPosition:
            "center center",

          pt: {
            xs: 0,
            md: 0,
          },

          pb: {
            xs: 7,
            md: 8,
          },
        }}
      >
        <Box
          aria-hidden="true"
          sx={{
            position: "absolute",
            inset: 0,

            background:
              "linear-gradient(90deg, rgba(0,0,0,0.05), rgba(255,255,255,0.16) 42%, rgba(0,0,0,0.04))",

            pointerEvents: "none",
          }}
        />

        <Container
          maxWidth={false}
          disableGutters
          sx={{
            position: "relative",
            zIndex: 1,

            maxWidth: 1280,

            px: {
              xs: 2,
              md: 3,
            },

            mx: "auto",
          }}
        >
          <Box
            sx={{
              width: "100%",

              maxWidth: {
                xs: "100%",
                sm: 720,
                md: 920,
              },

              mx: "auto",

              px: {
                xs: 2.5,
                sm: 4,
                md: 6.5,
              },

              pt: {
                xs: 3,
                md: 4.5,
              },

              /*
               * Keep the text content above the start of the bottom
               * oval curve. The extra bottom padding increases the
               * panel height without changing the oval shape itself.
               */
              pb: {
                xs: 10,
                md: 22,
              },

              bgcolor:
                "rgba(255, 255, 255, 0.78)",

              border:
                "1px solid rgba(255, 255, 255, 0.62)",

              boxShadow:
                "0 24px 70px rgba(0, 0, 0, 0.12)",

              backdropFilter:
                "blur(2px)",

              borderRadius: {
                xs: "0 0 100px 100px",
                md: "0 0 260px 260px / 0 0 190px 190px",
              },
            }}
          >
            <Box
              component="img"
              src={centerImage}
              alt={philosophy.title}
              sx={{
                display: "block",

                width: {
                  xs: "100%",
                  sm: "82%",
                  md: "78%",
                },

                maxWidth: 620,

                mx: "auto",

                mb: {
                  xs: 4,
                  md: 5,
                },

                aspectRatio:
                  "16 / 5.2",

                objectFit:
                  "cover",

                objectPosition:
                  "center center",

                borderRadius:
                  "999px",
              }}
            />

            {/* 企業理念 */}
            <Box
              sx={{
                textAlign: "left",
              }}
            >
              <Typography
                component="h2"
                sx={{
                  m: 0,

                  fontFamily: fonts,
                  fontWeight: 700,

                  fontSize: {
                    xs: 23,
                    md: 28,
                  },

                  lineHeight: 1.35,

                  color: accent,

                  mb: {
                    xs: 2,
                    md: 2.25,
                  },
                }}
              >
                {philosophy.title}
              </Typography>

              <Typography
                sx={{
                  fontFamily: fonts,

                  fontWeight: 500,

                  fontSize: {
                    xs: 15,
                    md: 18,
                  },

                  lineHeight: {
                    xs: 1.85,
                    md: 1.9,
                  },

                  color: "rgba(0, 0, 0, 0.78)",

                  mb: {
                    xs: 1.75,
                    md: 2,
                  },
                }}
              >
                {philosophy.subtitle}
              </Typography>

              <Typography
                sx={{
                  fontFamily: fonts,

                  fontWeight: 400,

                  fontSize: {
                    xs: 14.5,
                    md: 16,
                  },

                  lineHeight: {
                    xs: 1.9,
                    md: 2,
                  },

                  color: "rgba(0, 0, 0, 0.72)",
                }}
              >
                {philosophy.body.map((line, idx) => (
                  <React.Fragment key={idx}>
                    {line}
                    {idx < philosophy.body.length - 1 && <br />}
                  </React.Fragment>
                ))}
              </Typography>
            </Box>

            {/* Divider between 企業理念 and 企業願景 */}
            <Box
              aria-hidden="true"
              sx={{
                my: {
                  xs: 4,
                  md: 5.5,
                },

                borderBottom:
                  `1px solid ${alpha(accent, 0.36)}`,
              }}
            />

            {/* 企業願景 */}
            <Box
              sx={{
                textAlign: "left",
              }}
            >
              <Typography
                component="h2"
                sx={{
                  m: 0,

                  fontFamily: fonts,
                  fontWeight: 700,

                  fontSize: {
                    xs: 23,
                    md: 28,
                  },

                  lineHeight: 1.35,

                  color: accent,

                  mb: {
                    xs: 2,
                    md: 2.25,
                  },
                }}
              >
                {title}
              </Typography>

              <Typography
                sx={{
                  fontFamily: fonts,

                  fontWeight: 400,

                  fontSize: {
                    xs: 14.5,
                    md: 16,
                  },

                  lineHeight: {
                    xs: 1.9,
                    md: 2,
                  },

                  color: "rgba(0, 0, 0, 0.72)",
                }}
              >
                {bodyLines.map((line, idx) => (
                  <React.Fragment key={idx}>
                    {line}
                    {idx < bodyLines.length - 1 && <br />}
                  </React.Fragment>
                ))}
              </Typography>
            </Box>
          </Box>
        </Container>
      </Box>
    </Box>
  );
}